/**
 * Meridian SaaS — canonical permission client (Wave 1, iter 169).
 *
 * Fetches the role×permission and feature manifests from
 * `GET /api/auth/permissions` on every login/workspace-switch and
 * caches them in React state. The backend `permissions.py` matrix is
 * the single source of truth — the UI never hard-codes role checks.
 *
 * Public API:
 *   - <PermissionsProvider>  (wrap once, high in the tree)
 *   - usePermission("case.update") -> { allowed, reason }
 *   - useFeature("uk_filing_direct_hmrc") -> boolean
 *   - useRole() -> { raw, canonical }
 *   - <PermissionGate action="claim.submit"> children </PermissionGate>
 *   - <FeatureGate feature="uk_filing_direct_hmrc"> children </FeatureGate>
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';
import { useAuth } from '../context/AuthContext';

const PermissionsContext = createContext({
  loading: true,
  role: { raw: null, canonical: null },
  permissions: {},
  features: {},
  brokerScope: null,
  billing: null,
  refresh: () => {},
});

export const PermissionsProvider = ({ children }) => {
  const { user, activeWorkspace } = useAuth();
  const [state, setState] = useState({
    loading: true,
    role: { raw: null, canonical: null },
    permissions: {},
    features: {},
    brokerScope: null,
    billing: null,
  });

  const fetchManifest = useCallback(async () => {
    if (!user) {
      setState((s) => ({ ...s, loading: false, permissions: {}, features: {}, billing: null }));
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const cfg = activeWorkspace?.id
        ? { headers: { 'X-Workspace-Id': activeWorkspace.id } }
        : undefined;
      const res = await api.get('/auth/permissions', cfg);
      setState({
        loading: false,
        role: res.data.role || { raw: user.role, canonical: user.role },
        permissions: res.data.permissions || {},
        features: res.data.features || {},
        brokerScope: res.data.broker_scope || null,
        billing: res.data.billing || null,
        // Iter263 Phase T3 — partner_entitlements powers the Mode A
        // sidebar module gate. Non-partner callers get null.
        partner_entitlements: res.data.partner_entitlements || null,
      });
    } catch (err) {
      // Fail closed — UI shows nothing until manifest loads. Never
      // silently allow an action because the endpoint 500'd.
      setState({
        loading: false,
        role: { raw: user.role, canonical: user.role },
        permissions: {},
        features: {},
        brokerScope: null,
        billing: null,
      });
    }
  }, [user, activeWorkspace?.id]);

  useEffect(() => { fetchManifest(); }, [fetchManifest]);

  return (
    <PermissionsContext.Provider value={{ ...state, refresh: fetchManifest }}>
      {children}
    </PermissionsContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const usePermissionsContext = () => useContext(PermissionsContext);

export const useRole = () => useContext(PermissionsContext).role;

export const usePermission = (action) => {
  const { permissions, loading } = useContext(PermissionsContext);
  const allowed = permissions[action] === true;
  return {
    allowed,
    loading,
    reason: allowed ? null : 'role',
  };
};

export const useFeature = (feature) => {
  const { features, loading } = useContext(PermissionsContext);
  return { enabled: features[feature] === true, loading };
};

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

/**
 * PermissionGate — renders children when the current user has the given
 * permission. Otherwise renders `fallback` (default: null).
 *
 * Common patterns:
 *   <PermissionGate action="claim.submit">
 *     <Button>Submit to HMRC</Button>
 *   </PermissionGate>
 *
 *   <PermissionGate
 *     action="claim.submit"
 *     fallback={<LockedButton tooltip="Requires Merchant Admin" />}>
 *     <Button>Submit to HMRC</Button>
 *   </PermissionGate>
 */
export const PermissionGate = ({ action, fallback = null, loadingFallback = null, children }) => {
  const { allowed, loading } = usePermission(action);
  if (loading) return loadingFallback;
  return allowed ? <>{children}</> : fallback;
};

/**
 * FeatureGate — renders children only when the current workspace's
 * subscription includes the given feature. Independent of role.
 */
export const FeatureGate = ({ feature, fallback = null, loadingFallback = null, children }) => {
  const { enabled, loading } = useFeature(feature);
  if (loading) return loadingFallback;
  return enabled ? <>{children}</> : fallback;
};
